// Playbooks — long-form, top-of-funnel pillar pages that target high-intent
// category terms (e.g. "contextual ai marketing") and act as internal-link
// hubs to glossary terms, comparisons and product pages.

export interface PlaybookArticle {
  slug: string;
  primaryKeyword: string;
  searchVolume?: number;
  title: string;
  seoTitle: string;
  metaDescription: string;
  intro: string;
  updated: string;
  readingTime: string;
  /** Each chapter renders as a card in the hub ToC. */
  chapters: { id: string; title: string; summary: string }[];
  /** Markdown body — chapter titles must match `## <Chapter title>` exactly. */
  body: string;
  faq: { q: string; a: string }[];
  related?: { comparisons?: string[]; glossary?: string[]; insights?: string[] };
}

export const PLAYBOOKS_PATH = "/playbooks";

export const playbooks: PlaybookArticle[] = [
  {
    slug: "contextual-ai-marketing",
    primaryKeyword: "contextual ai marketing",
    searchVolume: 2400,
    updated: "2026-06-20",
    readingTime: "14 min read",
    title: "The contextual AI marketing playbook",
    seoTitle: "Contextual AI Marketing: The 2026 Playbook for Lean Teams",
    metaDescription:
      "A practical playbook for using contextual AI to plan, brief, ship and measure marketing — without the drift and editing tax of prompt-only generative tools.",
    intro:
      "Generative AI made content cheap. It also made on-brand, on-strategy content harder, not easier. Contextual AI fixes the gap by treating your brand, strategy and historical results as a persistent operating layer. This playbook explains the model, the workflow and the metrics that make it work for lean B2B marketing teams.",
    chapters: [
      {
        id: "what-is-contextual-ai-marketing",
        title: "What is contextual AI marketing?",
        summary: "The definition, why it differs from generative AI, and the business problem it solves.",
      },
      {
        id: "the-context-layer",
        title: "The context layer: what to capture, how often",
        summary: "The four inputs every contextual AI system needs and how to keep them fresh.",
      },
      {
        id: "the-workflow",
        title: "The four-stage workflow",
        summary: "Plan → Brief → Make → Measure — and what changes at each stage when context is live.",
      },
      {
        id: "metrics-that-prove-it-works",
        title: "Metrics that prove it works",
        summary: "Editing-tax, time-to-publish, brand-consistency score, and pipeline contribution.",
      },
      {
        id: "governance-and-trust",
        title: "Governance, trust and AEO",
        summary: "How to make the system safe to scale — and how AI Engine Optimisation fits in.",
      },
      {
        id: "30-60-90-day-plan",
        title: "30-60-90 day rollout plan",
        summary: "A staged plan that gets a lean team from pilot to operating model in a quarter.",
      },
    ],
    body: `**TL;DR** — Generative AI without context drifts off-brand and creates an editing tax that erases the speed gain. Contextual AI uses your strategy, brand, ICP and historical performance as persistent inputs to every brief, asset and report — so the work is on-brand by default. This playbook covers the model, the workflow, the metrics and a 30-60-90 day plan to get there.

## What is contextual AI marketing?

[Contextual AI marketing](/glossary/contextual-ai) is a way of running marketing where AI systems have continuous access to your business context — brand guidelines, strategic objectives, ICP, messaging pillars and past performance — and use that context as input to every plan, brief, asset and report.

The contrast is with **prompt-only generative AI**, where the model starts from a blank slate every request and the human is the only carrier of context. Prompt-only workflows look fast in a demo but silently degrade at scale:

- Briefs drift off positioning because nobody re-pasted the messaging pillars.
- Two writers using the same tool produce two voices.
- AI-generated reports cite the wrong KPI because the model never learned which one matters.

Contextual AI fixes this by moving context from human memory into the platform.

> The right unit of AI productivity is not "tokens per dollar". It is "approved assets per editing hour".

## The context layer: what to capture, how often

A working context layer has four inputs. None of them require a data-science team to maintain.

- **Strategy.** Your annual and quarterly objectives, written as outcomes (not activities). Refresh quarterly.
- **Brand.** Voice, tone, visual rules, approved claims, no-go list. Refresh on launch and on rebrand.
- **Audience.** Primary [ICP](/glossary/ideal-customer-profile), buyer roles, top jobs-to-be-done and objections. Refresh twice a year.
- **Performance.** What worked, what didn't, with the qualitative *why* — not just the numbers. Refresh weekly.

The discipline is "small, structured, fresh". A 200-page brand bible that nobody updates is worse than a one-page voice card that is current.

## The four-stage workflow

Contextual AI changes what happens at each stage of the marketing workflow, not just the writing step.

1. **Plan.** AI proposes the next quarter's campaign mix from your objectives, ICP and last quarter's results — not from a blank slide. The marketer edits, doesn't author from zero.
2. **Brief.** Every brief is auto-grounded in the campaign's strategic goal, the audience JTBD and the approved claims. See the [campaign brief template](/templates/campaign-brief-template) for the schema.
3. **Make.** Drafting (copy, design directions, ads) happens with brand voice and no-go rules enforced. Editors review for nuance, not for basic compliance.
4. **Measure.** Performance flows back into the context layer — so the next plan starts smarter than the last one. This is the loop that compounds.

## Metrics that prove it works

Contextual AI is worth measuring against the metrics that drive marketing throughput:

- **Editing tax** — minutes of human editing per AI-generated asset. Target: down 50 % within a quarter.
- **Time-to-publish** — calendar time from brief to live asset. Target: cut by a third.
- **Brand-consistency score** — % of assets that pass a brand-voice review without revision. Target: 85 %+.
- **Campaign → pipeline contribution** — % of pipeline you can attribute to a tracked campaign. Target: directionally positive, quarter-on-quarter.

If these metrics aren't moving, your context layer is too thin or too stale.

## Governance, trust and AEO

Two governance practices make the system safe to scale:

- **Zero data retention with model providers.** Your context is your competitive moat; do not let it leak into someone else's training set. See our [security page](/security) for our defaults.
- **Approved-claims registry.** A short, structured list of statements the AI is allowed to make about your product. Everything else gets flagged for human review.

Then there's **AEO — AI Engine Optimisation**. As more buyers research via ChatGPT, Perplexity, Claude and Google's AI Overviews, the goal shifts from "rank on the SERP" to "be cited in the answer". Contextual AI marketing supports AEO naturally because the same brand-grounded content that converts buyers also makes a clean source for LLMs to quote.

Practical AEO moves:

- Maintain a [glossary](/glossary) of category terms with crisp 45–60 word definitions.
- Allowlist GPTBot, ClaudeBot, PerplexityBot and Google-Extended in robots.txt.
- Publish llms.txt and llms-full.txt so models can find your canonical content.

## 30-60-90 day rollout plan

A staged rollout lets a lean team prove value before reorganising around it.

**Days 1–30 — Foundation.**

- Capture the four context inputs as one-page documents.
- Pick one workflow (we recommend campaign briefs) and use contextual AI for every brief that month.
- Baseline editing-tax and time-to-publish.

**Days 31–60 — Expand.**

- Add a second workflow (typically content drafting or email).
- Introduce the approved-claims registry.
- Start the weekly performance-into-context loop.

**Days 61–90 — Operationalise.**

- Move planning and reporting into the system.
- Set quarterly targets on the four metrics above.
- Decide what to stop doing — most teams find 2–3 legacy tools they can drop.

By day 90, contextual AI stops being a tool you're piloting and starts being the way the team works.

## Where STRAETCH fits

[STRAETCH](https://www.straetch.com) is a marketing operating system built around the contextual AI model described in this playbook. The platform captures the four context inputs, runs the four-stage workflow inside one workspace, and feeds performance back into the context layer automatically. If you want to see it end-to-end, [Start your 14-day free trial](https://app.straetch.com/auth?tab=signup) or compare against your current stack on our [platform comparison page](/comparison).`,
    faq: [
      {
        q: "What is contextual AI marketing?",
        a: "Contextual AI marketing uses your persistent business context — brand, strategy, ICP, messaging pillars and past performance — as inputs to every AI-assisted plan, brief, asset and report. The result is work that is on-brand and aligned by default, not after a manual editing pass.",
      },
      {
        q: "How is contextual AI different from generative AI?",
        a: "Generative AI starts from a blank slate every request and depends on the human to re-supply context. Contextual AI holds your brand, strategy and historical performance as persistent inputs across requests, so outputs stay aligned with the business and require less editing.",
      },
      {
        q: "What inputs does a contextual AI marketing system need?",
        a: "Four inputs are enough: a current strategy (objectives), a current brand voice and approved-claims list, a primary ICP and JTBD, and a refreshed view of recent performance with qualitative why-it-worked notes.",
      },
      {
        q: "How do I measure whether contextual AI is working?",
        a: "Track four metrics: editing tax (minutes of human edits per AI asset), time-to-publish (brief to live), brand-consistency score (% of assets passing voice review without revision), and campaign-to-pipeline contribution. If they aren't moving within a quarter, the context layer is too thin or too stale.",
      },
      {
        q: "What is AEO and how does it relate to contextual AI marketing?",
        a: "AEO — AI Engine Optimisation — is the practice of making your content easy for LLMs (ChatGPT, Perplexity, Claude, Google AI Overviews) to cite. Contextual AI marketing supports AEO naturally because the same brand-grounded, well-structured content that converts buyers also makes a clean, citable source for LLMs.",
      },
      {
        q: "How long does it take to roll out contextual AI for a lean marketing team?",
        a: "A 30-60-90 day plan is realistic: foundation and one workflow in month one, a second workflow plus governance in month two, planning and reporting plus retirement of legacy tools in month three.",
      },
    ],
    related: {
      comparisons: ["hubspot-alternatives", "clickup-alternatives-for-marketing"],
      glossary: ["contextual-ai", "ideal-customer-profile", "campaign-brief", "marketing-roi-dashboard"],
      insights: ["why-generative-ai-lets-marketers-down-contextual-ai"],
    },
  },
  {
    slug: "strategy-to-execution",
    primaryKeyword: "strategy to execution",
    searchVolume: 1300,
    updated: "2026-06-20",
    readingTime: "12 min read",
    title: "Strategy to execution: a playbook for lean marketing teams",
    seoTitle: "Strategy to Execution: The 2026 Playbook for Lean Marketing",
    metaDescription:
      "How lean B2B marketing teams close the gap between strategy and execution — the operating model, the artefacts, the cadence and the metrics that make plans actually ship.",
    intro:
      "Strategy is rarely the bottleneck. Translation is. Most lean marketing teams write a strong plan once a quarter and then watch it dilute as it travels into briefs, channels and reporting. This playbook covers the operating model that keeps strategy intact all the way to shipped work — without adding meetings.",
    chapters: [
      {
        id: "why-strategy-leaks",
        title: "Why strategy leaks on the way to execution",
        summary: "The three places intent gets lost between off-site and shipped asset.",
      },
      {
        id: "the-operating-model",
        title: "The strategy-to-execution operating model",
        summary: "Context layer + execution surface + feedback loop — and what each one actually is.",
      },
      {
        id: "the-artefacts",
        title: "The four artefacts that carry strategy",
        summary: "Strategy on a page, campaign brief, performance read-out, decision log.",
      },
      {
        id: "the-cadence",
        title: "The cadence that keeps it honest",
        summary: "Quarterly, monthly, weekly — what changes at each tempo and what does not.",
      },
      {
        id: "metrics",
        title: "Metrics that prove strategy is shipping",
        summary: "Strategy-alignment score, plan-to-ship ratio, decision latency, pipeline contribution.",
      },
      {
        id: "rollout",
        title: "A 90-day rollout for a lean team",
        summary: "From strategy doc to operating system in one quarter.",
      },
    ],
    body: `**TL;DR** — Most lean marketing teams do not have a strategy problem; they have a translation problem. Strategy lives in a deck; execution lives in briefs, channels and Slack. The fix is an operating model with three parts — a shared context layer, an execution surface that inherits it, and a feedback loop that updates it. This playbook shows what each looks like and how to roll it out in 90 days.

## Why strategy leaks on the way to execution

A strategy that works in the off-site dies in three predictable places:

- **At the brief.** The brief writer never re-reads the strategy. The brief inherits last quarter's instincts, not this quarter's plan.
- **At the channel.** The social manager, the SEO lead and the email owner each interpret the campaign through their channel's defaults. The unified message fragments.
- **At the read-out.** The dashboard reports activity (sessions, MQLs, posts shipped) instead of progress against the strategic objective. So next quarter starts blind.

These are not motivation problems. They are tooling and process problems. A team that solves them ships strategy without adding headcount.

## The strategy-to-execution operating model

The model has three components. Skip any one and the leaks return.

1. **A shared context layer.** Strategy, [ICP](/glossary/ideal-customer-profile), brand voice and [approved claims](/glossary/approved-claims-registry) live in one place every contributor — human and AI — reads from.
2. **An execution surface that inherits context.** Briefs, calendars, assets and outreach are produced inside a system that auto-grounds them in the context layer. Nobody has to remember to "include the positioning". See the [campaign brief template](/templates/campaign-brief-template).
3. **A feedback loop that updates context.** Performance — quantitative and qualitative — flows back into the context layer weekly. Next quarter's plan starts from this quarter's evidence, not from blank slides.

This is what a [marketing operating system](/glossary/marketing-operating-system) actually is. It is not a tool category; it is the wiring that makes the three components real.

## The four artefacts that carry strategy

The model needs four canonical artefacts. Each one is short. Each one is owned. Each one is current.

- **Strategy on a page.** One page: objective, ICP, positioning, top three bets for the quarter, what we will stop doing. Refresh quarterly.
- **Campaign brief.** One page per campaign: goal in pipeline terms, audience, single most important message, channels, success metric, kill criteria. Generated from the context layer, not from a blank template.
- **Performance read-out.** One page per cycle: what changed in pipeline, why, what we learned about the ICP and the message, what we will do next. Numbers + narrative.
- **Decision log.** A running list of the strategic decisions made and the evidence behind them. This is the single biggest hedge against drift — and against re-litigating the same call every quarter.

Four pages. Maintained. That is the entire spine of strategy-to-execution discipline.

## The cadence that keeps it honest

The artefacts only work if they live on a cadence the team actually keeps:

- **Quarterly.** Strategy on a page is re-written. The decision log is reviewed. The stop-doing list gets at least one entry.
- **Monthly.** Active campaigns are reviewed against their kill criteria. Underperformers get killed or re-briefed; over-performers get more budget.
- **Weekly.** Performance read-out for the week. New evidence is captured into the context layer the same day, not the same quarter.

The trap to avoid: replacing weekly performance read-outs with weekly status meetings. The artefact is the meeting. If you need a meeting on top, the artefact is too long or not trusted.

## Metrics that prove strategy is shipping

Activity metrics will not tell you whether strategy is making it through. Track four signals instead:

- **Strategy-alignment score.** A simple 1–5 rubric applied to every shipped asset: how clearly does this advance one of the top three bets? Target: 4+ average.
- **Plan-to-ship ratio.** Campaigns shipped this quarter / campaigns committed to in the plan. Target: 80 %+ — and a deliberate decision on every miss.
- **Decision latency.** Days from "we need to decide" to "decision in the log". Target: under 7 for tactical, under 21 for strategic.
- **Pipeline contribution from planned campaigns.** % of new pipeline attributable to campaigns named in the quarterly plan vs. ad-hoc work. Target: rising quarter-on-quarter.

If alignment and plan-to-ship are high but pipeline contribution is flat, the strategy itself is the problem — not the execution. That is a valuable thing to learn explicitly.

## A 90-day rollout for a lean team

You do not need a transformation programme. You need 90 days.

**Days 1–30 — Capture.** Write the strategy on a page. Audit the last quarter's campaigns against it (most will only partially align — that is the point). Stand up the context layer with the four inputs.

**Days 31–60 — Wire.** Move campaign briefs into a template that inherits from the context layer. Start the weekly performance read-out. Begin the decision log.

**Days 61–90 — Tighten.** Introduce kill criteria on every active campaign. Run the first monthly review against them. Publish the first quarterly read-out written from the artefacts rather than from memory.

By day 90 the team is operating from the same context, shipping against a named plan, and learning on a cadence. That is the operating model. Tooling — including [contextual AI](/glossary/contextual-ai) — sits on top of it and compounds the leverage.

## Where STRAETCH fits

[STRAETCH](https://www.straetch.com) is built to be the execution surface in this model. Strategy, brand and ICP live in one workspace; campaign briefs auto-ground in them; performance flows back automatically; AI does the drafting from the same context the strategy is written in. If your team currently runs strategy in a deck, briefs in Notion, work in ClickUp and reporting in Sheets, the [platform comparison](/comparison) shows what consolidating looks like.`,
    faq: [
      {
        q: "What does 'strategy to execution' mean for a marketing team?",
        a: "It is the operating discipline of turning a written marketing strategy into shipped work without losing intent. The common failure mode is a strategy that lives in a slide deck while execution happens in disconnected tools — producing output that is on-time but not on-strategy.",
      },
      {
        q: "Where does strategy usually leak before it becomes execution?",
        a: "In three places: at the brief (writers do not re-read the strategy), at the channel (each channel owner re-interprets the campaign through their defaults), and at the read-out (dashboards report activity instead of progress against the strategic goal).",
      },
      {
        q: "What is the minimum operating model that keeps strategy intact?",
        a: "Three parts: a shared context layer (strategy, ICP, brand, approved claims), an execution surface that inherits that context automatically, and a feedback loop that updates the context layer with performance. Remove any one component and intent leaks.",
      },
      {
        q: "Which artefacts carry strategy through execution?",
        a: "Four short, owned, current documents: strategy on a page, campaign brief, weekly performance read-out, and a running decision log. Each is one page. Together they replace most recurring marketing status meetings.",
      },
      {
        q: "How do I measure whether strategy is actually shipping?",
        a: "Four metrics: strategy-alignment score per shipped asset, plan-to-ship ratio (campaigns shipped vs. planned), decision latency (days from question to logged decision), and pipeline contribution from planned vs. ad-hoc work.",
      },
      {
        q: "How long does it take to roll out a strategy-to-execution model?",
        a: "A 90-day rollout is realistic for a lean B2B team: month one to capture and audit, month two to wire briefs and the weekly read-out, month three to introduce kill criteria and the first quarterly review run from the artefacts.",
      },
    ],
    related: {
      comparisons: ["marketing-project-management-software", "clickup-alternatives-for-marketing"],
      glossary: ["strategy-to-execution", "marketing-operating-system", "single-source-of-truth", "campaign-brief", "marketing-ops"],
      insights: [],
    },
  },
  {
    slug: "sales-and-marketing-alignment",
    primaryKeyword: "sales and marketing alignment",
    searchVolume: 1900,
    updated: "2026-06-20",
    readingTime: "11 min read",
    title: "Sales and marketing alignment: a playbook for lean B2B teams",
    seoTitle: "Sales and Marketing Alignment: The 2026 Playbook (B2B)",
    metaDescription:
      "A practical playbook for aligning sales and marketing in lean B2B teams — shared ICP, shared definitions, shared pipeline view, and the cadence that keeps it real.",
    intro:
      "Sales and marketing alignment is the most over-discussed and under-operationalised topic in B2B. For lean teams it is not a workshop output — it is a property of the system. This playbook covers the three artefacts, the cadence and the tooling that make alignment a steady state instead of a quarterly aspiration.",
    chapters: [
      {
        id: "what-alignment-actually-means",
        title: "What alignment actually means (and what it does not)",
        summary: "Three concrete signals that show alignment is real, not performative.",
      },
      {
        id: "the-shared-artefacts",
        title: "The three shared artefacts",
        summary: "Shared ICP, shared definition of a qualified opportunity, shared pipeline view.",
      },
      {
        id: "the-cadence",
        title: "The alignment cadence",
        summary: "Weekly pipeline review, monthly ICP review, quarterly plan — and what gets killed at each.",
      },
      {
        id: "where-it-breaks",
        title: "Where alignment breaks in lean teams",
        summary: "Five anti-patterns and the lightweight fixes that work without adding process.",
      },
      {
        id: "the-tooling-question",
        title: "The tooling question",
        summary: "Why a shared context layer beats a shared dashboard — and what to look for.",
      },
      {
        id: "rollout",
        title: "A 60-day rollout",
        summary: "From two teams running in parallel to one revenue motion in two months.",
      },
    ],
    body: `**TL;DR** — In a lean B2B team, sales-marketing alignment is not a workshop — it is the artefacts you both rely on, the cadence you both keep, and the system you both work inside. Three shared artefacts (ICP, qualified-opportunity definition, pipeline view) plus a weekly pipeline review plus a single context layer get you most of the way. This playbook covers the rollout.

## What alignment actually means (and what it does not)

Alignment is not "we get on well" or "we did a joint off-site". For lean B2B teams, alignment is real when three things are true at any moment:

- Both teams use the **same definition** of the target customer — same ICP, same disqualifiers, same "we are not for them" list.
- Both teams use the **same definition** of a qualified opportunity — same fields, same thresholds, same accountability for the handoff.
- Both teams look at the **same pipeline view** when asked "how are we doing?" — same source, same numbers, same rolling forecast.

If any of these is false, the rest of the alignment conversation is decoration.

## The three shared artefacts

These are the only artefacts you genuinely need. Keep them short. Keep them current.

- **Shared ICP.** One page. Industry, employee/revenue band, geography, technology signals, primary buyer role, primary job-to-be-done, explicit disqualifiers. Owned by marketing, ratified by sales. Refreshed twice a year against closed-won and closed-lost data. See [ICP](/glossary/ideal-customer-profile).
- **Shared definition of a qualified opportunity.** One page. The fields that must be populated for an opportunity to count as qualified (budget, authority, need, timing — or whatever your variant is), the thresholds, and who is accountable when the definition is not met. The single biggest source of sales-marketing friction is an ambiguous handoff; this artefact removes the ambiguity.
- **Shared pipeline view.** One dashboard. Pipeline by source, by stage, by ICP segment, by week. Marketing and sales open the same tab. There is no "marketing version" and "sales version" of the number.

That is it. Three pages and one view. Almost every alignment problem in a lean team is a missing or stale version of one of these.

## The alignment cadence

The artefacts only work on a cadence both teams keep.

- **Weekly — pipeline review.** 30 minutes. Both leads. The shared pipeline view is the only screen open. New opportunities discussed against the qualified-opportunity definition. Stuck opportunities discussed against the ICP. No slides.
- **Monthly — ICP review.** 45 minutes. Closed-won and closed-lost from the previous month walked through the ICP. The disqualifier list gets at least one update per quarter or the team is not learning.
- **Quarterly — joint plan.** Marketing's plan and sales's plan are written into the same document, not two. Targets are interlocking (pipeline → revenue) and the kill criteria are agreed jointly.

What you do not need: a weekly "alignment meeting" without one of the three artefacts on screen. That is theatre.

## Where alignment breaks in lean teams

Five anti-patterns account for almost every alignment failure in lean B2B:

- **Two ICPs.** Marketing optimises for one profile; sales pursues another. Fix: one ICP doc, owned by marketing, signed off by sales.
- **Lead vs. opportunity language.** Marketing reports MQLs; sales reports opportunities; nobody knows the conversion rate. Fix: pick the unit (we recommend "qualified opportunity") and have marketing report against it too.
- **Hidden re-qualification.** Sales silently re-qualifies every marketing-sourced opportunity, marketing never finds out, the qualification model never improves. Fix: every re-qualification logs a reason against the original lead.
- **Separate dashboards.** Marketing dashboard says "great month". Sales dashboard says "tough month". Both are right. Fix: one shared [marketing ROI dashboard](/glossary/marketing-roi-dashboard) reporting pipeline and revenue, not activity.
- **Brand voice drift in outbound.** Sales sequences are written without the marketing voice/claims. Buyers see two companies. Fix: sales sequences pull from the same [approved claims registry](/glossary/approved-claims-registry) and brand voice profile as marketing.

None of these need new headcount. They need shared artefacts and a system that enforces them.

## The tooling question

A shared dashboard helps. A shared context layer helps more.

A dashboard reports the past. A context layer governs the present: when sales drafts an outbound sequence, it pulls from the same ICP, voice and claims as the marketing campaign. When marketing writes a campaign brief, it pulls from the same pipeline reality as the sales weekly. The artefacts stop being PDFs and become live inputs to every action both teams take.

What to look for in tooling:

- One workspace where both teams can see strategy, campaigns, content and pipeline.
- A context layer (ICP, brand, claims) that is read by AI and humans on both sides.
- A pipeline view marketing genuinely uses — not just one sales updates.
- A handoff workflow that enforces the qualified-opportunity definition automatically.

This is the case for a [marketing operating system](/glossary/marketing-operating-system) over a stack of separate marketing and sales tools — particularly for teams under 30 in revenue functions.

## A 60-day rollout

You can move from "two teams in parallel" to "one revenue motion" in two months.

**Days 1–14 — Write the three artefacts.** ICP, qualified-opportunity definition, pipeline view. Marketing drafts, sales edits, both sign. Publish them in one place both teams open daily.

**Days 15–30 — Start the cadence.** First weekly pipeline review. First monthly ICP review scheduled. The artefacts are the only screens used.

**Days 31–60 — Wire the system.** Sales sequences and marketing assets pull from the same context layer. The shared pipeline view replaces all separate dashboards. The first joint quarterly plan is written.

By day 60 the teams are operating off the same artefacts, on the same cadence, inside the same system. Alignment stops being something you talk about and becomes the default state.

## Where STRAETCH fits

[STRAETCH](https://www.straetch.com) is built so that marketing strategy, content and the CRM share one context layer. The same ICP that informs the campaign brief informs the sales sequence. The same pipeline view appears in marketing's dashboard and the CRM. If alignment is currently a recurring meeting in your team, the [platform comparison](/comparison) shows what consolidating onto a shared operating layer looks like — and the [strategy-to-execution playbook](/playbooks/strategy-to-execution) covers the marketing-side discipline that complements it.`,
    faq: [
      {
        q: "What does sales and marketing alignment actually mean?",
        a: "It means both teams use the same definition of the target customer (ICP), the same definition of a qualified opportunity, and look at the same pipeline view. If any of those three is missing or stale, the rest of the alignment conversation is decoration.",
      },
      {
        q: "What artefacts are required for alignment in a lean B2B team?",
        a: "Three short, current, jointly owned documents: a one-page shared ICP, a one-page definition of a qualified opportunity (fields, thresholds, accountability), and a single shared pipeline view that both teams reference when asked how the business is doing.",
      },
      {
        q: "What cadence keeps sales and marketing aligned?",
        a: "A weekly 30-minute pipeline review against the shared pipeline view, a monthly 45-minute ICP review walking closed-won and closed-lost through the ICP, and a quarterly joint plan written into one document with interlocking targets.",
      },
      {
        q: "Where does sales-marketing alignment most often break in lean teams?",
        a: "Five anti-patterns: two different ICPs in use, mismatched lead vs. opportunity language, silent re-qualification by sales, separate marketing and sales dashboards, and brand voice drift in outbound sequences that ignore the marketing voice and claims.",
      },
      {
        q: "Do we need new tooling to align sales and marketing?",
        a: "Not necessarily, but a shared context layer beats a shared dashboard. When both teams pull from the same ICP, brand voice and approved claims — and look at the same pipeline view — alignment becomes a property of the system rather than a recurring meeting.",
      },
      {
        q: "How long does it take to roll out sales-marketing alignment?",
        a: "A 60-day rollout is realistic: two weeks to write the three artefacts, two weeks to start the weekly and monthly cadence, and one month to wire sales sequences and marketing assets to a shared context layer plus a single pipeline view.",
      },
    ],
    related: {
      comparisons: ["hubspot-alternatives", "marketing-project-management-software"],
      glossary: ["sales-and-marketing-alignment", "ideal-customer-profile", "marketing-roi-dashboard", "marketing-operating-system", "approved-claims-registry"],
      insights: [],
    },
  },
];

export function getPlaybookBySlug(slug: string): PlaybookArticle | undefined {
  return playbooks.find((p) => p.slug === slug);
}
